import { getSystem } from "../data/loadSystems";
import { hasArrivedForDelivery, isVipDeadlineExpired, type AcceptedMission } from "../acceptedMissions";
import { effectiveNow } from "../gameClock";
import { timeBonusMultiplier } from "../shipSpeed";
import { applyMissionCompletionStanding } from "../factionStanding";
import type { AppState } from "./state";

// Event-driven completion checking (16.34, revised): no background
// ticking, no stored "in progress" state. Hauling/VIP arrival and VIP's
// deadline are both checked opportunistically -- recomputed fresh every
// render, anywhere in the app, the same pattern already used for
// hop-distance (derive, don't cache). Combat/Mining completion, by
// contrast, happen through explicit player actions (Engage/Mine), not
// this pass, since a ship can only physically do one of those at a time.
// Returns true if any mission actually completed/failed -- callers use
// this to decide whether a save is warranted, rather than saving on every
// single render regardless of whether anything changed.
export function runOpportunisticChecks(appState: AppState): boolean {
  const now = effectiveNow(appState.clock, Date.now());
  const remaining: AcceptedMission[] = [];
  let changed = false;

  for (const mission of appState.acceptedMissions) {
    if (
      (mission.type === "Hauling" || mission.type === "VIP Transport") &&
      hasArrivedForDelivery(mission, appState.currentSystemId)
    ) {
      // Time bonus (16.56): linear 0.8x-1.2x against this route's own
      // par time, measured from acceptance to arrival.
      const actualTimeSeconds = (now - mission.acceptedAt) / 1000;
      const bonus = timeBonusMultiplier(actualTimeSeconds, mission.parTimeSeconds);
      const paidCr = Math.round(mission.rewardCr * bonus);
      appState.credits += paidCr;
      appState.stats.missionsCompleted++;
      appState.standing = applyMissionCompletionStanding(appState.standing, mission.posterFaction);
      appState.lastEvent = `${mission.type} mission complete -- earned ${paidCr} CR (${bonus.toFixed(2)}x time bonus).`;
      changed = true;
      continue;
    }

    if (mission.type === "VIP Transport" && isVipDeadlineExpired(mission, now)) {
      const destination = getSystem(mission.destinationSystemId)?.name ?? mission.destinationSystemId;
      appState.lastEvent = `VIP mission to ${destination} failed -- deadline expired, VIPs found alternate transport.`;
      changed = true;
      continue;
    }

    remaining.push(mission);
  }

  appState.acceptedMissions = remaining;
  return changed;
}
